import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { Container, Text, Title, Paper, Button, Loader, Stack, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const API_BASE_URL = window.location.hostname === 'alt-service.lingva-pro.org'
  ? window.location.origin + '/api'
  : (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api');

function GuidePage() {
  const { userId, teacherId } = useParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [language] = useState(() => {
    return localStorage.getItem('language') || (navigator.language.startsWith('ru') ? 'Русский' : 'English');
  });
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/knowledgeBaseArticle`);
        if (!response.ok) {
          throw new Error(`Ошибка HTTP: ${response.status}`);
        }
        const data = await response.json();
        const filteredArticles = data.filter(article => article.language === (language === 'Русский' ? 'ru_RU' : 'en_US'));
        setArticles(filteredArticles);
      } catch (error) {
        console.error(t('errorLoadingArticles'), error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [language]);

  const handleCardClick = (articleId) => {
    if (teacherId) {
      navigate(`/teacher/${teacherId}/guide/${articleId}`);
    } else {
      navigate(`/${userId}/guide/${articleId}`);
    }
  };

  const teacherArticles = articles.filter(article => article.cOnlyforteacher);
  const studentArticles = articles.filter(article => !article.cOnlyforteacher);

  const ArticleCard = ({ article, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
    >
      <Paper
        p="lg"
        radius="lg"
        className="glass-card"
        style={{ cursor: 'pointer' }}
        onClick={() => handleCardClick(article.id)}
      >
        <Text fw={700} size="sm">
          {article.name}
        </Text>
      </Paper>
    </motion.div>
  );

  return (
    <div style={{ backgroundColor: '#f0f9ff', minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <Container size="xl">
          <Group h={64} justify="space-between">
            <RouterLink
              to={teacherId ? `/teacher/${teacherId}` : `/${userId}`}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <img src="/logo.png" alt="LingvaPro Logo" style={{ height: 40 }} />
            </RouterLink>
            <Button
              variant="subtle"
              color="dark"
              component={RouterLink}
              to={teacherId ? `/teacher/${teacherId}` : `/${userId}`}
            >
              {t('returnToMain')}
            </Button>
          </Group>
        </Container>
      </header>

      <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
        <Title order={3} fw={700} mb="lg" ta="left">
          {t('knowledgeBase')}
        </Title>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Loader size="lg" color="var(--primary)" />
          </div>
        ) : (
          <>
            {teacherId && teacherArticles.length > 0 && (
              <>
                <Title order={3} fw={700} mb="lg">
                  {t('forTeachers')}
                </Title>
                <Stack gap="md">
                  {teacherArticles.map((article, index) => (
                    <ArticleCard key={article.id} article={article} index={index} />
                  ))}
                </Stack>
              </>
            )}
            {teacherId && studentArticles.length > 0 && (
              <>
                <Title order={3} fw={700} mt={40} mb="lg">
                  {t('forEveryone')}
                </Title>
                <Stack gap="md">
                  {studentArticles.map((article, index) => (
                    <ArticleCard key={article.id} article={article} index={index} />
                  ))}
                </Stack>
              </>
            )}
            {!teacherId && studentArticles.length > 0 && (
              <Stack gap="md">
                {studentArticles.map((article, index) => (
                  <ArticleCard key={article.id} article={article} index={index} />
                ))}
              </Stack>
            )}
            {articles.length === 0 && (
              <Text ta="center" c="var(--text-muted)" mt="lg">
                {t('noArticlesFound')}
              </Text>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default GuidePage;
